import React, { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import Selector from 'components/ui/Selector'
import DropdownLang from 'components/DropdownLang'
import MyPagination from 'components/ui/MyPagination'
import MyRefetchButton from 'components/ui/MyRefetchButton'
import { useForm } from './index'
import { LevelOptions } from 'config'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { X } from 'lucide-react'
import MyCheckbox from 'components/ui/MyCheckbox'
import { ContentTagFilter } from 'components/template-tags'

const orderOptions = [
  { code: 'updateTime', name: 'updateTime' },
  { code: 'createTime', name: 'createTime' },
  { code: 'dailyCount.reCreateCount', name: 'reCreateCount-daily' },
  { code: 'reCreateCount', name: 'reCreateCount-total' },
  { code: 'reportCount', name: 'reportCount' },
]

const LEVEL_FILTER_OPTIONS = LevelOptions.filter((item) => item.code !== 0)

const FilterLabel = ({ htmlFor, children }) => (
  <label htmlFor={htmlFor} className="shrink-0 text-sm font-medium">
    {children}
  </label>
)

const DataTableToolbar = ({ mutate, nextAfter }) => {
  const [, setSearchParams] = useSearchParams()
  const {
    level,
    setLevel,
    order,
    setOrder,
    afters,
    after,
    goAfter,
    lang,
    setLang,
    langOnly,
    setLangOnly,
    id,
    setId,
    author,
    setAuthor,
    contentTag,
    setContentTag: setContentTagStore,
    sortByPageReCreateCount,
    setSortByPageReCreateCount,
  } = useForm()

  const setContentTag = useCallback(
    (value) => {
      const next = value || null
      setContentTagStore(next)
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev)
          if (next) params.set('contentTag', next)
          else params.delete('contentTag')
          return params
        },
        { replace: true },
      )
    },
    [setContentTagStore, setSearchParams],
  )

  const handleIdChange = (e) => {
    setId(e.target.value)
  }

  const handleClearId = () => {
    setId('')
  }

  const syncAuthor = useCallback(
    (value) => {
      const next = value || ''
      setAuthor(next)
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev)
          if (next) params.set('author', next)
          else params.delete('author')
          return params
        },
        { replace: true },
      )
    },
    [setAuthor, setSearchParams],
  )

  const handleAuthorChange = (e) => {
    syncAuthor(e.target.value)
  }

  const handleClearAuthor = () => {
    syncAuthor('')
  }

  const hasLevel = useMemo(() => level != null && level !== 0, [level])
  const isMultiId =
    String(id || '')
      .split(/[\s,，;；]+/)
      .filter((item) => item.trim().length > 0).length > 1

  return (
    <div className="mb-2 flex w-full flex-wrap items-center gap-x-4 gap-y-2">
      <div className="flex shrink-0 items-center gap-4">
        {!isMultiId && (
          <MyPagination
            nextAfter={nextAfter}
            after={after}
            goAfter={goAfter}
            afters={afters}
          />
        )}
        <MyRefetchButton onClick={() => mutate()} />
      </div>

      <div className="flex items-center gap-2">
        <FilterLabel htmlFor="tid-filter">TID:</FilterLabel>
        <div className="relative">
          <Input
            id="tid-filter"
            type="text"
            placeholder="TID，多个用逗号或空格分隔"
            value={id}
            onChange={handleIdChange}
            className="w-[28rem] max-w-full pr-8"
          />
          {id && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearId}
              className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <FilterLabel htmlFor="painter-filter">Painter:</FilterLabel>
        <div className="relative">
          <Input
            id="painter-filter"
            type="text"
            placeholder="Painter"
            value={author}
            onChange={handleAuthorChange}
            className="w-48"
          />
          {author && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAuthor}
              className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <FilterLabel htmlFor="lang-filter">Lang:</FilterLabel>
        <DropdownLang value={lang} update={setLang} />
      </div>
      <MyCheckbox
        id="lang-only-filter"
        checked={langOnly}
        disabled={!lang}
        onCheckedChange={(checked) => setLangOnly(checked === true)}
        label="only in this country"
      />

      <div className="flex items-center gap-2">
        <FilterLabel htmlFor="order">Order:</FilterLabel>
        <Selector
          id="order"
          datas={orderOptions}
          update={setOrder}
          value={order}
          labelName="order"
          className={contentTag ? 'pointer-events-none opacity-50' : undefined}
        />
      </div>

      <div className="flex items-center gap-2">
        <FilterLabel htmlFor="level">Level:</FilterLabel>
        <div className="relative">
          <Selector
            id="level"
            datas={LEVEL_FILTER_OPTIONS}
            update={setLevel}
            value={hasLevel ? level : null}
            labelName="level"
            placeholder="All"
          />
          {hasLevel ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              title="Clear level"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setLevel(null)
              }}
              className="absolute right-8 top-1/2 h-6 w-6 -translate-y-1/2 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <FilterLabel htmlFor="contentTag">Tag:</FilterLabel>
        <ContentTagFilter
          id="contentTag"
          value={contentTag}
          onChange={setContentTag}
        />
      </div>

      <MyCheckbox
        id="sort-by-page-reCreateCount"
        checked={sortByPageReCreateCount}
        onCheckedChange={(checked) =>
          setSortByPageReCreateCount(checked === true)
        }
        label="reCreateCount-total (page)"
      />
    </div>
  )
}

export default DataTableToolbar
